import { useRef, useState } from "react";
import { Button, Card, Group, Text } from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AdminApiError, adminErrText, adminGet, adminSend, adminUpload } from "../../api.ts";
import { BulkCreateDrawer } from "./prizes/BulkCreateDrawer.tsx";
import { CreatePrizeModal } from "./prizes/CreatePrizeModal.tsx";
import { EditPrizeModal } from "./prizes/EditPrizeModal.tsx";
import { PrizeTable } from "./prizes/PrizeTable.tsx";
import { SimulateDrawer } from "./prizes/SimulateDrawer.tsx";
import {
  type BulkRow,
  type CreateForm,
  type EditForm,
  buildI18nMap,
  buildPrizeMetadata,
  emptyBulkRow,
  genPrizeCode,
  readPrizeMetadata,
  showIssueErrors,
  useProbabilities,
} from "./prizes/types.ts";
import { fieldNameZh } from "../../lib/fieldNames.ts";

type PrizeRow = import("./prizes/types.ts").PrizeRow;

export function PrizesTab(props: {
  readonly botId: string;
  readonly languages: readonly string[];
}) {
  const { botId } = props;
  const qc = useQueryClient();
  const existingCodesRef = useRef<ReadonlySet<string>>(new Set());
  const [createOpen, setCreateOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkRows, setBulkRows] = useState<BulkRow[]>([emptyBulkRow()]);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState("");
  const [bulkSaving, setBulkSaving] = useState(false);
  const [simOpen, setSimOpen] = useState(false);
  const [simResult, setSimResult] = useState<Array<{
    prizeId: string;
    code: string;
    expected: number;
    actual: number;
    deviation: number;
  }> | null>(null);

  const [createImageUploading, setCreateImageUploading] = useState(false);
  const [editImageUploading, setEditImageUploading] = useState(false);

  const createForm = useForm<CreateForm>({
    initialValues: {
      code: "",
      name: "",
      description: "",
      type: "thanks",
      weight: 1,
      valueAmount: 0,
      valueCurrency: "",
      stock: "",
      dailyLimit: "",
      isJackpot: false,
      requiresShipping: false,
      wgPay: false,
      auditRate: 1,
      claimInstructions: {},
      imageUrl: "",
      titleMode: "text",
      displayColor: "",
      displayIcon: "",
      displayOrder: 0,
      i18nName: {},
      i18nSubtitle: {},
      startsAt: "",
      endsAt: "",
    },
    validate: {
      code: (v) => {
        if (v.trim().length === 0) return "请填写 code";
        if (existingCodesRef.current.has(v.trim()))
          return "该 code 已被现有奖品占用（含已禁用），请换一个";
        return null;
      },
      name: (v) => (v.trim().length === 0 ? "必填" : null),
    },
  });

  const editForm = useForm<EditForm>({
    initialValues: {
      type: "thanks",
      name: "",
      description: "",
      weight: 1,
      valueAmount: 0,
      valueCurrency: "",
      stock: "",
      dailyLimit: "",
      isEnabled: true,
      isJackpot: false,
      requiresShipping: false,
      wgPay: false,
      auditRate: 1,
      claimInstructions: {},
      imageUrl: "",
      titleMode: "text",
      displayColor: "",
      displayIcon: "",
      displayOrder: 0,
      i18nName: {},
      i18nSubtitle: {},
      startsAt: "",
      endsAt: "",
    },
  });

  const prizes = useQuery({
    queryKey: ["bot", botId, "prizes"],
    queryFn: () => adminGet<{ items: PrizeRow[] }>(`/bots/${botId}/prizes`),
  });

  const createPrize = useMutation({
    mutationFn: (v: CreateForm) => {
      const { wgPay, auditRate, claimInstructions, ...rest } = v;
      return adminSend("POST", `/bots/${botId}/prizes`, {
        ...rest,
        description: v.description.trim() === "" ? undefined : v.description.trim(),
        stock: v.stock === "" ? undefined : Number(v.stock),
        dailyLimit: v.dailyLimit === "" ? undefined : Number(v.dailyLimit),
        valueCurrency: v.valueCurrency === "" ? undefined : v.valueCurrency,
        imageUrl: v.imageUrl === "" ? undefined : v.imageUrl,
        displayColor: v.displayColor === "" ? undefined : v.displayColor,
        displayIcon: v.displayIcon === "" ? undefined : v.displayIcon,
        prizeMetadata: buildPrizeMetadata(wgPay, claimInstructions, auditRate),
        i18nName: buildI18nMap(v.i18nName),
        i18nSubtitle: buildI18nMap(v.i18nSubtitle),
        startsAt: v.startsAt === "" ? undefined : new Date(v.startsAt).toISOString(),
        endsAt: v.endsAt === "" ? undefined : new Date(v.endsAt).toISOString(),
      });
    },
    onSuccess: () => {
      notifications.show({ color: "green", message: "已添加" });
      setCreateOpen(false);
      createForm.reset();
      void qc.invalidateQueries({ queryKey: ["bot", botId, "prizes"] });
    },
    onError: showIssueErrors,
  });

  const patchPrize = useMutation({
    mutationFn: (args: { id: string; payload: EditForm }) => {
      const { wgPay, auditRate, claimInstructions, ...rest } = args.payload;
      // PATCH 里清空字段须发 null（发 undefined 会被服务端跳过 -> 旧值清不掉）。
      return adminSend("PATCH", `/bots/${botId}/prizes/${args.id}`, {
        ...rest,
        description: rest.description.trim() === "" ? null : rest.description.trim(),
        stock: rest.stock === "" ? null : Number(rest.stock),
        dailyLimit: rest.dailyLimit === "" ? null : Number(rest.dailyLimit),
        valueCurrency: rest.valueCurrency === "" ? null : rest.valueCurrency,
        imageUrl: rest.imageUrl === "" ? null : rest.imageUrl,
        displayColor: rest.displayColor === "" ? null : rest.displayColor,
        displayIcon: rest.displayIcon === "" ? null : rest.displayIcon,
        prizeMetadata: buildPrizeMetadata(wgPay, claimInstructions, auditRate),
        i18nName: buildI18nMap(rest.i18nName),
        i18nSubtitle: buildI18nMap(rest.i18nSubtitle),
        startsAt: rest.startsAt === "" ? null : new Date(rest.startsAt).toISOString(),
        endsAt: rest.endsAt === "" ? null : new Date(rest.endsAt).toISOString(),
      });
    },
    onSuccess: () => {
      notifications.show({ color: "green", message: "已更新" });
      setEditingId(null);
      void qc.invalidateQueries({ queryKey: ["bot", botId, "prizes"] });
    },
    onError: showIssueErrors,
  });

  const deletePrize = useMutation({
    mutationFn: (id: string) => adminSend("DELETE", `/bots/${botId}/prizes/${id}`),
    onSuccess: () => {
      notifications.show({ color: "green", message: "已删除" });
      void qc.invalidateQueries({ queryKey: ["bot", botId, "prizes"] });
    },
    onError: (e) =>
      notifications.show({ color: "red", message: adminErrText((e as AdminApiError).code) }),
  });

  const toggleEnabled = useMutation({
    mutationFn: (args: { id: string; isEnabled: boolean }) =>
      adminSend("PATCH", `/bots/${botId}/prizes/${args.id}`, { isEnabled: args.isEnabled }),
    // 乐观更新：点开关立即反映到当前列表，不等网络往返
    onMutate: async (vars) => {
      await qc.cancelQueries({ queryKey: ["bot", botId, "prizes"] });
      const prev = qc.getQueryData<{ items: PrizeRow[] }>(["bot", botId, "prizes"]);
      qc.setQueryData<{ items: PrizeRow[] }>(["bot", botId, "prizes"], (old) =>
        old === undefined
          ? old
          : {
              ...old,
              items: old.items.map((p) =>
                p.id === vars.id ? { ...p, is_enabled: vars.isEnabled } : p,
              ),
            },
      );
      return { prev };
    },
    onError: (err, _vars, ctx) => {
      // 失败回滚到改前快照
      if (ctx?.prev !== undefined) qc.setQueryData(["bot", botId, "prizes"], ctx.prev);
      showIssueErrors(err);
    },
    onSuccess: (_d, vars) => {
      notifications.show({ color: "green", message: vars.isEnabled ? "已上线" : "已下线" });
    },
    // 兜底与服务端对齐（config_version 等）
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["bot", botId, "prizes"] });
    },
  });

  const resetStock = useMutation({
    mutationFn: (id: string) => adminSend("POST", `/bots/${botId}/prizes/${id}/reset-daily-stock`),
    onSuccess: () => notifications.show({ color: "green", message: "日额度已重置" }),
    onError: (e) =>
      notifications.show({ color: "red", message: adminErrText((e as AdminApiError).code) }),
  });

  const simulate = useMutation({
    mutationFn: () =>
      adminSend<{
        results: Array<{
          prizeId: string;
          code: string;
          expected: number;
          actual: number;
          deviation: number;
        }>;
      }>("POST", `/bots/${botId}/prizes/simulate`, { iterations: 10000 }),
    onSuccess: (r) => {
      setSimResult(r.results);
      setSimOpen(true);
    },
  });

  const existingCodes = new Set((prizes.data?.items ?? []).map((p) => p.code));
  existingCodesRef.current = existingCodes;

  const probs = useProbabilities(prizes.data?.items ?? []);
  const prizeNames = new Map((prizes.data?.items ?? []).map((p) => [p.id, p.name]));
  const editing = prizes.data?.items.find((p) => p.id === editingId) ?? null;

  // ---- image upload helpers ----
  const handleCreateImageUpload = async (file: File | null): Promise<void> => {
    if (file === null) return;
    setCreateImageUploading(true);
    try {
      const r = await adminUpload(file);
      createForm.setFieldValue("imageUrl", r.url);
    } catch (e) {
      notifications.show({
        color: "red",
        message: adminErrText((e as AdminApiError).code, "图片上传失败"),
      });
    } finally {
      setCreateImageUploading(false);
    }
  };

  const handleEditImageUpload = async (file: File | null): Promise<void> => {
    if (file === null) return;
    setEditImageUploading(true);
    try {
      const r = await adminUpload(file);
      editForm.setFieldValue("imageUrl", r.url);
    } catch (e) {
      notifications.show({
        color: "red",
        message: adminErrText((e as AdminApiError).code, "图片上传失败"),
      });
    } finally {
      setEditImageUploading(false);
    }
  };

  const handleBulkImageUpload = async (idx: number, file: File | null): Promise<void> => {
    if (file === null) return;
    setBulkRows((prev) => prev.map((r, i) => (i === idx ? { ...r, _uploading: true } : r)));
    try {
      const result = await adminUpload(file);
      setBulkRows((prev) =>
        prev.map((r, i) => (i === idx ? { ...r, imageUrl: result.url, _uploading: false } : r)),
      );
    } catch (e) {
      notifications.show({
        color: "red",
        message: adminErrText((e as AdminApiError).code, "图片上传失败"),
      });
      setBulkRows((prev) => prev.map((r, i) => (i === idx ? { ...r, _uploading: false } : r)));
    }
  };

  // ---- bulk row helpers ----
  const updateBulkRow = (idx: number, patch: Partial<BulkRow>): void => {
    setBulkRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const addBulkRow = (): void => {
    setBulkRows((prev) => [...prev, emptyBulkRow()]);
  };

  const copyLastBulkRow = (): void => {
    setBulkRows((prev) => {
      if (prev.length === 0) return [...prev, emptyBulkRow()];
      const last = prev[prev.length - 1];
      if (last === undefined) return [...prev, emptyBulkRow()];
      const { _error: _e, ...rest } = last;
      void _e;
      return [...prev, { ...rest, code: genPrizeCode(), name: "" }];
    });
  };

  const removeBulkRow = (idx: number): void => {
    setBulkRows((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleBulkSave = async (): Promise<void> => {
    setBulkSaving(true);
    let allOk = true;
    const updated = [...bulkRows];
    for (let i = 0; i < updated.length; i++) {
      const row = updated[i];
      if (row === undefined) continue;
      try {
        await adminSend("POST", `/bots/${botId}/prizes`, {
          code: row.code,
          name: row.name,
          type: row.type,
          weight: row.weight,
          valueAmount: row.valueAmount,
          stock: row.stock === "" ? undefined : Number(row.stock),
          dailyLimit: row.dailyLimit === "" ? undefined : Number(row.dailyLimit),
          imageUrl: row.imageUrl === "" ? undefined : row.imageUrl,
          displayColor: row.displayColor === "" ? undefined : row.displayColor,
          requiresShipping: row.requiresShipping,
        });
        const { _error: _e, ...rowWithoutError } = row;
        void _e;
        updated[i] = rowWithoutError;
      } catch (e) {
        allOk = false;
        const err = e as AdminApiError;
        if (err.code === "INVALID_REQUEST" && err.issues.length > 0) {
          updated[i] = {
            ...row,
            _error: err.issues
              .map((iss) => `${fieldNameZh(iss.path.join("."))}：${iss.message}`)
              .join("；"),
          };
        } else {
          updated[i] = { ...row, _error: adminErrText(err.code, err.message) };
        }
      }
    }
    setBulkRows(updated);
    setBulkSaving(false);
    if (allOk) {
      notifications.show({ color: "green", message: "批量创建完成" });
      setBulkOpen(false);
      setBulkRows([emptyBulkRow()]);
      void qc.invalidateQueries({ queryKey: ["bot", botId, "prizes"] });
    } else {
      notifications.show({ color: "orange", message: "部分行保存失败，红色行请修正后重试" });
      setBulkRows(updated.filter((r) => r._error !== undefined));
    }
  };

  const handleEditSubmit = (v: EditForm): void => {
    patchPrize.mutate({ id: editingId!, payload: v });
  };

  const handleEditRow = (row: PrizeRow): void => {
    const toInputVal = (iso: string | null): string => {
      if (iso === null) return "";
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return "";
      const pad = (n: number) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    };
    const meta = readPrizeMetadata(row.prize_metadata);
    editForm.setValues({
      type: row.type,
      name: row.name,
      description: row.description ?? "",
      weight: row.weight,
      valueAmount: row.value_amount,
      valueCurrency: row.value_currency ?? "",
      stock: row.stock ?? "",
      dailyLimit: row.daily_limit ?? "",
      isEnabled: row.is_enabled,
      isJackpot: row.is_jackpot,
      requiresShipping: row.requires_shipping,
      wgPay: meta.wgPay,
      auditRate: meta.auditRate,
      claimInstructions: meta.claimInstructions,
      imageUrl: row.image_url ?? "",
      titleMode: row.title_mode,
      displayColor: row.display_color ?? "",
      displayIcon: row.display_icon ?? "",
      displayOrder: row.display_order,
      i18nName: row.i18n_name ?? {},
      i18nSubtitle: row.i18n_subtitle ?? {},
      startsAt: toInputVal(row.starts_at),
      endsAt: toInputVal(row.ends_at),
    });
    setEditingId(row.id);
  };

  return (
    <>
      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Group justify="space-between" mb="md">
          <Text fw={600} size="lg">
            奖品池
          </Text>
          <Group gap="xs">
            <Button
              variant="outline"
              onClick={() => simulate.mutate()}
              loading={simulate.isPending}
            >
              蒙特卡洛模拟
            </Button>
            <Button variant="outline" onClick={() => setBulkOpen(true)}>
              批量创建
            </Button>
            <Button
              onClick={() => {
                createForm.reset();
                createForm.setFieldValue("code", genPrizeCode());
                setCreateOpen(true);
              }}
            >
              添加
            </Button>
          </Group>
        </Group>
        <PrizeTable
          isLoading={prizes.isLoading}
          items={prizes.data?.items ?? []}
          probs={probs}
          resetStock={resetStock}
          deletePrize={deletePrize}
          toggleEnabled={toggleEnabled}
          onEdit={handleEditRow}
        />
      </Card>

      <CreatePrizeModal
        opened={createOpen}
        onClose={() => setCreateOpen(false)}
        form={createForm}
        isPending={createPrize.isPending}
        imageUploading={createImageUploading}
        onSubmit={(v) => createPrize.mutate(v)}
        onImageUpload={(file) => void handleCreateImageUpload(file)}
        languages={props.languages}
        existingCodes={existingCodes}
      />

      <EditPrizeModal
        editingId={editingId}
        editingName={editing?.name ?? ""}
        onClose={() => setEditingId(null)}
        form={editForm}
        isPending={patchPrize.isPending}
        imageUploading={editImageUploading}
        onSubmit={handleEditSubmit}
        onImageUpload={(file) => void handleEditImageUpload(file)}
        languages={props.languages}
      />

      <BulkCreateDrawer
        opened={bulkOpen}
        onClose={() => setBulkOpen(false)}
        bulkRows={bulkRows}
        bulkSaving={bulkSaving}
        pasteOpen={pasteOpen}
        pasteText={pasteText}
        onSetPasteOpen={setPasteOpen}
        onSetPasteText={setPasteText}
        onUpdateBulkRow={updateBulkRow}
        onAddBulkRow={addBulkRow}
        onCopyLastBulkRow={copyLastBulkRow}
        onRemoveBulkRow={removeBulkRow}
        onBulkSave={() => void handleBulkSave()}
        onBulkImageUpload={(idx, file) => void handleBulkImageUpload(idx, file)}
        onSetBulkRows={setBulkRows}
        existingCodes={existingCodes}
      />

      <SimulateDrawer
        opened={simOpen}
        onClose={() => setSimOpen(false)}
        simResult={simResult}
        prizeNames={prizeNames}
      />
    </>
  );
}
